import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import styled, { ThemeProvider } from 'styled-components';
import { Theme } from '../../theme';
import { Prediction } from '../../types/predictions';
import { media } from '../../theme';
import { handleWheelZoom, withConstraintsBypass, withPredictionViewportAdjustment } from './zoomConfig.js';
import { renderAllPredictions } from './predictions/PredictionRenderer';
import { renderUserPredictionPoint, renderContestEntriesDots, ContestEntry } from './predictions/ContestTargetLine';
import priceWebSocket from '../../services/priceWebSocket';
import { formatPrice } from '../../utils/priceUtils';
import { PriceLegend } from '../PriceLegend';
import { DrawingMode, PredictionPoint, DrawingResult, DrawingModeType } from './DrawingMode';
import { DrawingConfirmModal } from './DrawingConfirmModal';

// Check for screenshot mode from URL
const isScreenshotMode = new URLSearchParams(window.location.search).get('screenshot') === 'true';

const CHART_MARGIN = isScreenshotMode 
  ? { top: 5, right: 15, bottom: 5, left: 50 }  // Minimal margins - almost no bottom
  : { top: 30, right: 60, bottom: 70, left: 70 }; // Normal margins

const StyledContainer = styled.div<{ $isScreenshotMode?: boolean }>`
  position: ${props => props.$isScreenshotMode ? 'absolute' : 'relative'};
  top: ${props => props.$isScreenshotMode ? '0' : 'auto'};
  left: ${props => props.$isScreenshotMode ? '0' : 'auto'};
  height: ${props => props.$isScreenshotMode ? '100vh' : '100%'};
  min-height: ${props => props.$isScreenshotMode ? '100vh' : '400px'};
  background: ${props => props.theme.surface};
  border-radius: ${props => props.$isScreenshotMode ? '0' : '12px'};
  padding: ${props => props.$isScreenshotMode ? '0' : '16px'};
  margin: ${props => props.$isScreenshotMode ? '0' : 'auto'};
  width: ${props => props.$isScreenshotMode ? '100vw' : '100%'};
  box-sizing: border-box;
  /* Allow prediction labels to extend horizontally beyond chart boundaries */
  overflow-x: ${props => props.$isScreenshotMode ? 'hidden' : 'visible'};
  overflow-y: hidden;
  overscroll-behavior: contain;
  /* Allow touch events to pass through to D3 handlers */
  touch-action: none;
  isolation: isolate;

  .axis text {
    fill: ${props => props.theme.chartText} !important;
    font-size: 12px;
  }

  .axis path,
  .axis line {
    stroke: ${props => props.theme.chartGrid};
  }

  .y-axis text,
  .y-axis path,
  .y-axis line {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    cursor: ns-resize !important;
  }

  .y-axis-zone {
    cursor: ns-resize !important;
    user-select: none;
    &:hover {
      background: ${props => props.theme.chartGrid};
      opacity: 0.1;
    }
  }

  .y-axis-zoom-zone {
    cursor: ns-resize;
  }

  /* Prevent scroll propagation */
  &, svg {
    overscroll-behavior: none;
    touch-action: none;
  }
  
  /* Allow SVG interaction overlay to handle touch */
  svg .interaction-overlay {
    touch-action: none;
  }

  .chart-area {
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  }

  /* Prediction visibility control - enhanced for smoother transitions */
  .prediction-inactive {
    opacity: 0 !important;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    pointer-events: none;
  }
  
  /* Dashboard mode: show all dots even when inactive */
  &[data-show-all-dots="true"] .prediction-inactive .price-marker,
  &[data-show-all-dots="true"] .prediction-inactive .sentiment-indicator {
    opacity: 0.6 !important;
    visibility: visible !important;
  }
  
  .prediction-active {
    opacity: 1;
    visibility: visible;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }
  
  /* Status colors */
  .prediction-success {
    stroke: ${props => props.theme.successText};
  }
  
  .prediction-failed {
    stroke: ${props => props.theme.failedText};
  }

  /* 🐰 Easter Egg Animations */
  @keyframes bounce {
    0% { transform: translateY(0px); }
    100% { transform: translateY(-10px); }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

interface PriceChartProps {
  predictions: Prediction[];
  activePredictions?: number[];  // Array of prediction IDs, not Prediction objects
  chartData: any[];
  isDarkMode: boolean;
  theme: Theme;
  treasureHuntMode?: boolean;
  currentTreasureHuntPrediction?: Prediction;
  showAllDots?: boolean; // Dashboard mode: show all dots, only active card
  preserveZoomOnSelection?: boolean; // Only pan/zoom if selected item is completely outside view
  compactMode?: boolean; // Landscape mobile: smaller cards
  isScreenshotMode?: boolean; // Screenshot mode: simplified rendering for captures
  userContestPrediction?: number | null; // User's contest prediction price
  userHandle?: string; // User's X handle for label
  contestEntries?: ContestEntry[]; // All contest entries to display on chart
}

export const PriceChart: React.FC<PriceChartProps> = ({
  predictions,
  activePredictions,
  chartData,
  isDarkMode,
  theme,
  treasureHuntMode = false,
  currentTreasureHuntPrediction,
  showAllDots = false,
  preserveZoomOnSelection = false,
  compactMode = false,
  isScreenshotMode = false,
  userContestPrediction = null,
  userHandle,
  contestEntries = []
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipData, setTooltipData] = useState<{ x: number; y: number; price: number; date: string } | null>(null);
  
  // ✅ NEW: Track container dimensions to trigger re-render on resize
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  
  // ✅ NEW: Track which predictions we've already adjusted viewport for (one-time adjustment)
  const [adjustedPredictions, setAdjustedPredictions] = useState<Set<number>>(new Set());
  
  const initialScaleRef = useRef<{ x: any; y: any }>();
  
  // Fix the currentPriceIndicatorRef type to allow null
  const currentPriceIndicatorRef = useRef<{
    circle: d3.Selection<SVGCircleElement, unknown, null, undefined>;
    label: d3.Selection<SVGTextElement, unknown, null, undefined>;
  } | null>(null);
  
  // Add missing refs and variables
  const pulseCircleRef = useRef<d3.Selection<SVGCircleElement, unknown, null, undefined> | null>(null);
  const latestPriceRef = useRef<any>(null); // Store latest price data
  const scalesRef = useRef<{ xScale: any, yScale: any } | null>(null); // Store current scales
  const chartAreaRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null); // Store chart area
  const processedDataRef = useRef<Array<{time: Date, value: number}>>([]);  // Store processed chart data
  const lineGeneratorRef = useRef<d3.Line<{ time: Date; value: number }> | null>(null); // Store line generator for live updates
  
  // ✅ ZOOM PERSISTENCE: Store zoom state separately so it survives re-renders
  const zoomStateRef = useRef<{ 
    xDomain: [Date, Date] | null, 
    yDomain: [number, number] | null,
    dataHash: string | null  // Track if data actually changed
  }>({ xDomain: null, yDomain: null, dataHash: null });
  
  // ✅ FIX: Store activePredictions in a ref so updateChart always has the latest value
  const activePredictionsRef = useRef<number[]>(activePredictions || []);
  
  // ✅ FIX: Keep the ref synchronized with the latest activePredictions value
  useEffect(() => {
    activePredictionsRef.current = activePredictions || [];
  }, [activePredictions]);

  // 🐰 EASTER EGG LOGIC: Detect when Easter Bunny prediction (ID 331) is active
  useEffect(() => {
    const isEasterBunnyActive = activePredictions?.includes(331);
    
    if (isEasterBunnyActive && !easterEggSpawnedRef.current) {
      // Check if user already found the easter egg in this session
      const alreadyFound = localStorage.getItem('proofofprice-easter-egg-2026');
      if (alreadyFound) {
        try {
          const foundData = JSON.parse(alreadyFound);
          if (foundData.found) return;
        } catch {
          // Old format, still skip
          return;
        }
      }
      
      // Mark as spawned to prevent respawning
      easterEggSpawnedRef.current = true;
      
      // Spawn Easter eggs! (21 like Bitcoin's supply)
      const newEggs = Array.from({ length: 21 }, (_, i) => ({
        id: `egg-${i}`,
        x: Math.random() * (dimensions.width || 800),
        y: -50 - Math.random() * 200, // Start above the chart
        vx: (Math.random() - 0.5) * 2, // Small horizontal velocity
        vy: Math.random() * 2 + 1, // Downward velocity
        collected: false,
        color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'][i % 6]
      }));
      
      setEasterEggs(newEggs);
      
      // Show instructions popup
      setShowEasterEggInstructions(true);
      setTimeout(() => setShowEasterEggInstructions(false), 4000); // Auto-hide after 4 seconds
      
      // Start animation
      const animate = () => {
        setEasterEggs(prevEggs => 
          prevEggs.map(egg => {
            if (egg.collected) return egg;
            
            const newY = egg.y + egg.vy;
            const newX = egg.x + egg.vx;
            
            // Remove eggs that fall too far below chart
            if (newY > (dimensions.height || 600) + 100) {
              return { ...egg, collected: true };
            }
            
            // Bounce off sides
            let newVx = egg.vx;
            if (newX <= 0 || newX >= (dimensions.width || 800)) {
              newVx = -egg.vx;
            }
            
            return {
              ...egg,
              x: Math.max(0, Math.min(newX, dimensions.width || 800)),
              y: newY,
              vx: newVx,
              vy: egg.vy + 0.1 // Slight gravity
            };
          })
        );
        
        easterEggAnimationRef.current = requestAnimationFrame(animate);
      };
      
      animate();
    } else if (!isEasterBunnyActive && easterEggSpawnedRef.current) {
      // Clean up when prediction becomes inactive
      easterEggSpawnedRef.current = false;
      setEasterEggs([]);
      if (easterEggAnimationRef.current) {
        cancelAnimationFrame(easterEggAnimationRef.current);
      }
    }
  }, [activePredictions, dimensions]);

  // 🐰 EASTER EGG CLEANUP: Clean up animation on unmount
  useEffect(() => {
    return () => {
      if (easterEggAnimationRef.current) {
        cancelAnimationFrame(easterEggAnimationRef.current);
      }
    };
  }, []);
  
  // Add refs for live price elements
  const livePriceElementsRef = useRef<{
    line: d3.Selection<SVGLineElement, unknown, null, undefined> | null;
    label: d3.Selection<SVGTextElement, unknown, null, undefined> | null;
    timeLabel: d3.Selection<SVGTextElement, unknown, null, undefined> | null;
  }>({ line: null, label: null, timeLabel: null });
  
  // Add state for live price
  const [livePrice, setLivePrice] = useState<number | null>(null);

  // ✅ DRAWING MODE: State for click-to-place prediction input
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState<PredictionPoint[]>([]);
  const [drawingModeType, setDrawingModeType] = useState<'points' | 'range' | 'pattern' | 'freehand'>('points');
  const [drawingFreehandPaths, setDrawingFreehandPaths] = useState<Array<{ points: { x: number; y: number }[]; color: string }>>([]);
  const [drawingImageBase64, setDrawingImageBase64] = useState<string | null>(null);
  const [showDrawingConfirm, setShowDrawingConfirm] = useState(false);

  // 🐰 EASTER EGG STATE
  const [easterEggs, setEasterEggs] = useState<Array<{
    id: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    collected: boolean;
    color: string;
  }>>([]);
  const [easterEggCollected, setEasterEggCollected] = useState(0);
  const [showEasterEggPopup, setShowEasterEggPopup] = useState(false);
  const [showEasterEggInstructions, setShowEasterEggInstructions] = useState(false);
  const [bitcoinExplosions, setBitcoinExplosions] = useState<Array<{
    id: string;
    x: number;
    y: number;
    timestamp: number;
  }>>([]);
  const easterEggAnimationRef = useRef<number>();
  const easterEggSpawnedRef = useRef<boolean>(false);
  
  // ✅ DRAWING MODE: Ref to access drawing mode state from D3 event handlers
  const isDrawingModeRef = useRef(false);
  useEffect(() => {
    isDrawingModeRef.current = isDrawingMode;
    // Update overlay cursor when drawing mode changes
    if (svgRef.current) {
      const overlay = d3.select(svgRef.current).select('.interaction-overlay');
      overlay.style('cursor', isDrawingMode ? 'crosshair' : 'grab');
    }
  }, [isDrawingMode]);

  // ✅ NEW: ResizeObserver to detect container size changes (browser zoom, window resize)
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        // Only update if dimensions actually changed (avoid infinite loops)
        setDimensions(prev => {
          if (Math.abs(prev.width - width) > 1 || Math.abs(prev.height - height) > 1) {
            return { width, height };
          }
          return prev;
        });
      }
    });
    
    resizeObserver.observe(containerRef.current);
    
    // Set initial dimensions
    setDimensions({
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight
    });
    
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const preventScroll = (e: WheelEvent) => {
      if (containerRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    // Add event listener with passive: false to allow preventDefault
    window.addEventListener('wheel', preventScroll, { passive: false });
    
    return () => window.removeEventListener('wheel', preventScroll);
  }, []);

  // Connect to WebSocket for live price updates
  useEffect(() => {
    const handlePriceUpdate = (price: number) => {
      setLivePrice(price);
    };
    
    // Connect to WebSocket for live price updates
    priceWebSocket.on('price', handlePriceUpdate);
    priceWebSocket.connect();
    
    return () => {
      priceWebSocket.off('price', handlePriceUpdate);
    };
  }, []);



  useEffect(() => {
    if (!containerRef.current || !chartData.length) {
      return;
    }

    // Use CHART_MARGIN which adjusts for screenshot mode
    const margin = CHART_MARGIN;
    
    // In screenshot mode, use viewport dimensions directly for guaranteed fullscreen
    const containerWidth = isScreenshotMode ? window.innerWidth : containerRef.current.clientWidth;
    const containerHeight = isScreenshotMode ? window.innerHeight : containerRef.current.clientHeight;
    
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    // Clear previous content
    const svg = d3.select(svgRef.current)
      .html('')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
      
    // Process chart data - convert strings to Date objects
    const processedData = chartData.map(d => ({
      time: new Date(d.time),
      value: d.value
    })).sort((a, b) => a.time.getTime() - b.time.getTime());

    // Get current date and the latest data point
    const currentDate = new Date();
    const latestDataPoint = processedData[processedData.length - 1]; // Fix: Define latestDataPoint
    const earliestDate = d3.min(processedData, d => d.time)!;

    // Calculate time range to display
    // We want the current date to be about 70% from the left edge
    const displayTimeRange = (currentDate.getTime() - earliestDate.getTime()) * (1 / 0.7);
    
    // Calculate start date for our viewport
    const startViewportDate = new Date(currentDate.getTime() - displayTimeRange * 0.7);
    
    // Calculate end date for our viewport (current date plus some future space)
    const endViewportDate = new Date(currentDate.getTime() + displayTimeRange * 0.3);

    // Create x scale with the calculated range
    const xScale = d3.scaleTime()
      .domain([
        startViewportDate < earliestDate ? earliestDate : startViewportDate,
        endViewportDate
      ])
      .range([0, width]);

    // Get the max price from the data
    const maxPrice = d3.max(processedData, d => d.value) || 70000;

    // Always start with a Y-axis that shows up to 300k for consistency
    const DEFAULT_Y_MAX = 300000;
    
    // Create y scale with intelligent scaling for treasure hunt mode
    const yScale = d3.scaleLinear()
      .domain([-1000, DEFAULT_Y_MAX]) // Start with default range
      .range([height, 0]);

    // 🗺️ TREASURE HUNT: Intelligent scaling for extreme predictions
    if (treasureHuntMode && currentTreasureHuntPrediction) {
      const pred = currentTreasureHuntPrediction;
      const priceStr = pred.price_prediction || '';
      
      // Extract numeric values from prediction
      const numbers = priceStr.match(/[\d,]+/g);
      const prices = numbers ? numbers.map(n => parseFloat(n.replace(/,/g, ''))) : [];
      
      if (prices.length > 0) {
        const minPred = Math.min(...prices);
        const maxPred = Math.max(...prices);
        const currentPrice = processedData.length > 0 ? processedData[processedData.length - 1].value : 100000;
        
        // For extreme predictions (like Hal Finney's $10M), create a layered view
        if (maxPred > 1000000) {
          // Create distinct zones: current price area + prediction area
          // This prevents tweet cards from overlapping the price line
          const currentPriceZone = currentPrice;
          const predictionZone = maxPred;
          
          // Calculate viewport that gives each zone proper visual space
          const zoneRatio = predictionZone / currentPriceZone;
          
          if (zoneRatio > 100) {
            // Massive difference (like Hal's 10M vs current ~100k)
            // Use log-like scaling to show both zones clearly
            const yMin = currentPrice * 0.3;  // Show current price context
            const yMax = maxPred * 1.1;       // Show prediction with small buffer
            yScale.domain([yMin, yMax]);
          } else {
            // Large but manageable difference
            const yMin = Math.min(currentPrice * 0.5, minPred * 0.8);
            const yMax = maxPred * 1.2;
            yScale.domain([yMin, yMax]);
          }
        } else {
          // For normal predictions, use a focused view around the prediction
          const buffer = Math.max(50000, (maxPred - minPred) * 0.5);
          const yMin = Math.max(-1000, minPred - buffer);
          const yMax = maxPred + buffer;
          yScale.domain([yMin, yMax]);
        }
      }
    }

    // Store initial scales
    initialScaleRef.current = { 
      x: xScale.copy(), 
      y: yScale.copy() 
    };

    // Create a group for Y-axis elements
    const yAxisGroup = svg.append('g')
      .attr('class', 'y-axis-group');

    // Add Y-axis zoom area
    yAxisGroup.append('rect')
      .attr('class', 'y-axis-zone')
      .attr('x', -margin.left)
      .attr('width', margin.left)
      .attr('height', height)
      .attr('fill', 'transparent')
      .style('cursor', 'ns-resize')
      .on('wheel', (event) => {
        event.preventDefault();
        const delta = event.deltaY;
        const factor = delta > 0 ? 1.1 : 0.9;
        
        const [, max] = yScale.domain();
        // Allow unlimited zoom out by removing the cap
        yScale.domain([0, max * factor]);
        
        // ✅ ZOOM PERSISTENCE: Save zoom state after Y-axis zoom
        zoomStateRef.current.xDomain = xScale.domain() as [Date, Date];
        zoomStateRef.current.yDomain = yScale.domain() as [number, number];

        // Use updateChart to handle all updates
        updateChart();
      });
      
    // Add the axis on top
    const yAxis = yAxisGroup.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale)
        .tickFormat(d => formatPrice(d as number))
      )
      .call(g => {
        g.selectAll('.tick text').attr('fill', theme.chartText);
        g.selectAll('.tick line').attr('stroke', theme.chartGrid);
        g.select('.domain').attr('stroke', theme.chartGrid);
      });

    // Add x-axis with formatted dates
    svg.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .tickFormat((d: Date | d3.NumberValue) => {
          // Check if d is a Date before formatting it
          if (d instanceof Date) {
            return d3.timeFormat('%b %Y')(d);
          }
          return '';
        })
      )
      .call(g => {
        g.selectAll('.tick text')
          .attr('fill', theme.chartText)
          .attr('transform', 'rotate(-45)')
          .attr('text-anchor', 'end');
        g.selectAll('.tick line').attr('stroke', theme.chartGrid);
        g.select('.domain').attr('stroke', theme.chartGrid);
      });

    // Add grid lines for better readability
    svg.append('g')
      .attr('class', 'grid x-grid')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-height)
        .tickFormat(() => '')
      )
      .call(g => {
        g.selectAll('.tick line')
          .attr('stroke', theme.chartGrid)
          .attr('opacity', 0.3);
        g.select('.domain').remove();
      });

    svg.append('g')
      .attr('class', 'grid y-grid')
      .call(d3.axisLeft(yScale)
        .tickSize(-width)
        .tickFormat(() => '')
      )
      .call(g => {
        g.selectAll('.tick line')
          .attr('stroke', theme.chartGrid)
          .attr('opacity', 0.3);
        g.select('.domain').remove();
      });

    // Create clipping path
    svg.append('defs')
      .append('clipPath')
      .attr('id', 'chart-area-clip')
      .append('rect')
      // Extend the clipping rectangle to include negative values
      // Instead of starting at y=0, start at a negative offset and double the height
      .attr('x', 0) // Keep x at 0
      .attr('y', -height) // Start at negative height to show values below zero
      .attr('width', width)
      .attr('height', height * 2); // Double the height to ensure we see values below 0

    // Create chart area with clipping
    const chartArea = svg.append('g')
      .attr('clip-path', 'url(#chart-area-clip)')
      .attr('data-width', width)
      .attr('data-height', height);
    
    // Store refs for use in other effects
    chartAreaRef.current = chartArea;
    processedDataRef.current = processedData;

    // Draw the line
    const line = d3.line<{ time: Date; value: number }>()
      .x(d => xScale(d.time))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX); // Add curve for smoother line
    
    // Store line generator for live price updates
    lineGeneratorRef.current = line;

    chartArea.append('path')
      .datum(processedData)
      .attr('class', 'price-line')
      .attr('fill', 'none')
      .attr('stroke', theme.primary)
      .attr('stroke-width', 2)
      .attr('d', line);

    // Add watermark to the chart
    svg.append('g')
      .attr('class', 'chart-watermark')
      .attr('transform', `translate(${width / 2}, ${height / 2})`)
      .style('pointer-events', 'none') // Make sure it doesn't capture mouse events
      .call(g => {
        // Add "proofofprice.com" watermark text
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('font-size', '24px')
          .attr('font-weight', 'bold')
          .attr('fill', theme.text)
          .attr('opacity', 0.07)
          .attr('transform', 'rotate(-20)')
          .text('proofofprice.com');
      });

    // Add a "current price" indicator with the latest price point
    if (latestDataPoint) {
      // Get the current price and chart dimensions - fix the undefined latestPrice variable
      const currentPrice = latestDataPoint?.value || 0;
      const currentPriceY = yScale(currentPrice);
      const chartWidth = width - margin.left - margin.right;

      // Store the latest price data and scales for animation updates - fix latestPrice reference
      latestPriceRef.current = latestDataPoint;
      
      // ✅ ZOOM PERSISTENCE: Check if data actually changed (compare hash)
      const currentDataHash = `${chartData.length}-${chartData[0]?.time}-${chartData[chartData.length-1]?.time}`;
      const dataChanged = zoomStateRef.current.dataHash !== currentDataHash;
      
      // ✅ ZOOM PERSISTENCE: Restore previous zoom state if data hasn't changed
      if (!dataChanged && zoomStateRef.current.xDomain && zoomStateRef.current.yDomain) {
        xScale.domain(zoomStateRef.current.xDomain);
        yScale.domain(zoomStateRef.current.yDomain);
        console.log('🔍 Restored zoom state after re-render');
      } else if (dataChanged) {
        // Data changed - reset zoom state
        zoomStateRef.current = { xDomain: null, yDomain: null, dataHash: currentDataHash };
        console.log('📊 Data changed - reset zoom state');
      }
      
      scalesRef.current = { xScale, yScale };

      // Create the fixed circle for current price indicator
      const fixedCircle = svg.append('circle')
        .attr('class', 'current-price-indicator')
        .attr('cx', chartWidth - 10)
        .attr('cy', currentPriceY)
        .attr('r', 6)
        .style('fill', theme.primary)
        .style('stroke', theme.background)
        .style('stroke-width', 2)
        .style('pointer-events', 'none');

      // Create the pulsating circle for animation
      const pulseCircle = svg.append('circle')
        .attr('class', 'price-pulse-indicator')
        .attr('cx', chartWidth - 10)
        .attr('cy', currentPriceY)
        .attr('r', 4)
        .style('fill', '#ff6600')
        .style('pointer-events', 'none');

      // Create vivid pulsating animation function that updates position dynamically
      const animateVividPulse = () => {
        if (!pulseCircleRef.current || !latestPriceRef.current || !scalesRef.current) return;
        
        // Get current scales and latest price
        const { xScale: currentXScale, yScale: currentYScale } = scalesRef.current;
        const currentLatestPrice = latestPriceRef.current;
        
        // Calculate current position based on current scales - fix timestamp access
        const currentX = currentXScale(new Date(currentLatestPrice.time));
        const currentY = currentYScale(currentLatestPrice.value);
        
        // Update pulse circle position and restart animation
        pulseCircleRef.current
          .attr('cx', currentX)
          .attr('cy', currentY)
          .attr('r', 4)
          .attr('opacity', 0.8)
          .style('fill', '#ff6600')
          .transition()
          .duration(1000)
          .ease(d3.easeLinear)
          .attr('r', 12)
          .attr('opacity', 0)
          .on('end', () => {
            // Restart the animation with updated position
            animateVividPulse();
          });
      };

      // Create price label for the current price indicator
      const priceLabel = svg.append('text')
        .attr('class', 'current-price-label')
        .attr('x', chartWidth - 10 + 15) // ✅ Changed from - 10 to + 15 (move to right side)
        .attr('y', currentPriceY - 8)
        .attr('text-anchor', 'start') // ✅ Changed from 'end' to 'start' for left-aligned text
        .style('fill', theme.primary)
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(formatPrice(currentPrice));

      // Store references to both circles and label
      currentPriceIndicatorRef.current = {
        circle: fixedCircle, // Keep the fixed circle as the main reference
        label: priceLabel
      };
      
      // Store pulse circle reference separately for updates
      pulseCircleRef.current = pulseCircle;

      // Start the animation
      animateVividPulse();
    }

    // Add floating live price indicator in upper right corner (professional design)
    const livePriceGroup = svg.append('g')
      .attr('class', 'live-price-floating-indicator')
      .attr('opacity', 0); // Hidden initially
    
    // Create a modern floating price card in the upper right
    const priceCardWidth = 120;
    const priceCardHeight = 50;
    const cardMargin = 15;
    
    const livePriceCard = livePriceGroup.append('rect')
      .attr('rx', 8) // More rounded corners for modern look
      .attr('ry', 8)
      .attr('fill', 'rgba(0, 0, 0, 0.85)') // Dark background with high opacity
      .attr('x', width - priceCardWidth - cardMargin)
      .attr('y', cardMargin)
      .attr('height', priceCardHeight)
      .attr('width', priceCardWidth)
      .attr('stroke', theme.primary) // Orange border
      .attr('stroke-width', 1.5)
      .attr('filter', 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.3))'); // Subtle shadow
      
    // "LIVE" indicator with pulsing animation
    const liveIndicator = livePriceGroup.append('text')
      .attr('x', width - priceCardWidth/2 - cardMargin)
      .attr('text-anchor', 'middle')
      .attr('y', cardMargin + 15)
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .attr('fill', theme.primary)
      .attr('class', 'live-indicator-text')
      .text('● LIVE');
    
    // Add pulsing animation to the live indicator
    liveIndicator.append('animate')
      .attr('attributeName', 'opacity')
      .attr('values', '1;0.3;1')
      .attr('dur', '2s')
      .attr('repeatCount', 'indefinite');
    
    // Price label (main text)
    const livePriceLabel = livePriceGroup.append('text')
      .attr('x', width - priceCardWidth/2 - cardMargin)
      .attr('text-anchor', 'middle')
      .attr('y', cardMargin + 32)
      .attr('font-size', '16px')
      .attr('font-weight', 'bold')
      .attr('fill', 'white')
      .attr('class', 'live-price-text');
    
    // Optional: Small time indicator (very subtle)
    const liveTimeLabel = livePriceGroup.append('text')
      .attr('x', width - priceCardWidth/2 - cardMargin)
      .attr('text-anchor', 'middle')
      .attr('y', cardMargin + 45)
      .attr('font-size', '9px')
    // Store references (no line needed for floating indicator)
    livePriceElementsRef.current = {
      line: null, // No line in floating design
      label: livePriceLabel,
      timeLabel: liveTimeLabel as any
    };

    // Update function to handle all chart updates
    const updateChart = () => {
      
      // ✅ FIX: Use ref to get the latest activePredictions value
      const currentActivePredictions = activePredictionsRef.current;
      
      // Get all active prediction values for viewport adjustment
      const activeYValues = (currentActivePredictions)
        .map(id => {
          const pred = predictions.find(p => p.id === id);
          if (!pred) return null;
          
          // Extract all relevant prediction values
          const values: number[] = [];
          
          if (pred.prediction_type === 'target') {
            const priceValue = pred.price_target !== undefined ? Number(pred.price_target) :
                             pred.price_min !== undefined ? Number(pred.price_min) :
                             pred.price_prediction !== undefined ? Number(pred.price_prediction) : null;
            if (priceValue && isFinite(priceValue)) values.push(priceValue);
          } else if (pred.prediction_type === 'top' || pred.prediction_type === 'bottom') {
            const priceValue = pred.price_target !== undefined ? Number(pred.price_target) :
                             pred.price_min !== undefined ? Number(pred.price_min) :
                             pred.price_max !== undefined ? Number(pred.price_max) :
                             pred.price_prediction !== undefined ? Number(pred.price_prediction) : null;
            if (priceValue && isFinite(priceValue)) values.push(priceValue);
          } else if (pred.prediction_type === 'range') {
            if (pred.price_min !== undefined && isFinite(Number(pred.price_min))) values.push(Number(pred.price_min));
            if (pred.price_max !== undefined && isFinite(Number(pred.price_max))) values.push(Number(pred.price_max));
          } else if (pred.prediction_type === 'pricetimerange') {
            if (pred.price_min !== undefined && isFinite(Number(pred.price_min))) values.push(Number(pred.price_min));
          } else if (pred.prediction_type === 'bet') {
            // For bet predictions, use the price_prediction value (where the bet line is drawn)
            if (pred.price_prediction !== undefined && isFinite(Number(pred.price_prediction))) {
              values.push(Number(pred.price_prediction));
            }
          }
          
          return values;
        })
        .filter((vals): vals is number[] => vals !== null && vals.length > 0)
        .flat();

      // ✅ NEW: Get all active prediction dates for X-axis viewport adjustment
      const activeXDates = (currentActivePredictions)
        .map(id => {
          const pred = predictions.find(p => p.id === id);
          if (!pred) return null;
          
          const dates: Date[] = [];
          
          // For pricetimerange predictions, use start_date and end_date
          if (pred.prediction_type === 'pricetimerange' && pred.start_date && pred.end_date) {
            dates.push(new Date(pred.start_date));
            dates.push(new Date(pred.end_date));
          }
          // For bet predictions, use start_date and end_date to show the full bet timeline
          else if (pred.prediction_type === 'bet' && pred.start_date && pred.end_date) {
            dates.push(new Date(pred.start_date));
            dates.push(new Date(pred.end_date));
          }
          // For other predictions, use target_time or end_date
          else if (pred.target_time) {
            dates.push(new Date(pred.target_time));
          }
          else if (pred.end_date) {
            dates.push(new Date(pred.end_date));
          }
          
          return dates.filter(date => !isNaN(date.getTime()));
        })
        .filter(dates => dates && dates.length > 0)
        .flat();
      
      
      // ✅ SMART: Adjust viewport when active predictions change (including removals)
      const currentActivePredictionSet = new Set(currentActivePredictions);
      const previousActivePredictionSet = adjustedPredictions;
      
      // Check if the active predictions have changed (additions or removals)
      const hasChanged = currentActivePredictionSet.size !== previousActivePredictionSet.size ||
        !Array.from(currentActivePredictionSet).every(id => previousActivePredictionSet.has(id));
      
      if (hasChanged && (activeYValues.length > 0 || activeXDates.length > 0)) {
        
        // 🔧 preserveZoomOnSelection mode: Only adjust if selection is completely outside current view
        if (preserveZoomOnSelection) {
          const [currentXMin, currentXMax] = xScale.domain();
          const [currentYMin, currentYMax] = yScale.domain();
          
          // Check if any active prediction date/value is within current view
          const validDateTimes = activeXDates
            .filter((d): d is Date => d !== null && !isNaN(d.getTime()))
            .map(d => d.getTime());
          
          const isXInView = validDateTimes.length === 0 || validDateTimes.some(t => 
            t >= currentXMin.getTime() && t <= currentXMax.getTime()
          );
          const isYInView = activeYValues.length === 0 || activeYValues.some(v => 
            v >= currentYMin && v <= currentYMax
          );
          
          // If selection is at least partially visible, don't adjust viewport
          if (isXInView && isYInView) {
            setAdjustedPredictions(new Set(currentActivePredictions));
            // Skip viewport adjustment, continue with rendering
          } else {
            // Selection is completely outside view - pan to show it
            withPredictionViewportAdjustment(() => {
              if (validDateTimes.length > 0) {
                const targetDate = new Date(validDateTimes[0]);
                const currentRange = currentXMax.getTime() - currentXMin.getTime();
                // Center the view on the target date
                const newXMin = new Date(targetDate.getTime() - currentRange / 2);
                const newXMax = new Date(targetDate.getTime() + currentRange / 2);
                
                // Apply constraint
                const earliestDate = d3.min(processedData, d => d.time)!;
                xScale.domain([
                  newXMin < earliestDate ? earliestDate : newXMin,
                  newXMax
                ]);
              }
              
              if (activeYValues.length > 0) {
                const targetValue = activeYValues[0];
                const currentRange = currentYMax - currentYMin;
                // Center the view on the target value
                yScale.domain([
                  targetValue - currentRange / 2,
                  targetValue + currentRange / 2
                ]);
              }
            });
            setAdjustedPredictions(new Set(currentActivePredictions));
          }
        } else {
          // Original behavior: always adjust viewport to fit predictions
          // Use bypass constraints to allow viewport adjustment
          withPredictionViewportAdjustment(() => {
            let needsUpdate = false;
            
            // Y-axis adjustment (existing logic)
            if (activeYValues.length > 0) {
              const [currentMin, currentMax] = yScale.domain();
              const minActiveValue = Math.min(...activeYValues);
              const maxActiveValue = Math.max(...activeYValues);
              
              
              let newMin = currentMin;
              let newMax = currentMax;
              
              // Expand downward if predictions are below current view
              if (minActiveValue < currentMin) {
                newMin = minActiveValue < 0 ? minActiveValue * 1.2 : minActiveValue * 0.8;
                needsUpdate = true;
              }
              
              // Expand upward if predictions are above current view
              if (maxActiveValue > currentMax) {
                // Add MUCH more padding for high-value predictions to ensure name labels are visible
                const paddingMultiplier = maxActiveValue >= 5000000 ? 2.0 : 1.4; // 100% padding for $5M+ predictions, 40% for others
                newMax = maxActiveValue * paddingMultiplier;
                needsUpdate = true;
              }
              
              // Apply Y-axis changes
              if (needsUpdate) {
                yScale.domain([newMin, newMax]);
              }
            }

            // ✅ NEW: X-axis adjustment
            if (activeXDates.length > 0) {
              const [currentXMin, currentXMax] = xScale.domain();
              // Filter out any null dates and then map to timestamps
              const validDateTimes = activeXDates
                .filter((d): d is Date => d !== null && !isNaN(d.getTime()))
                .map(d => d.getTime());
              const minActiveDate = new Date(Math.min(...validDateTimes));
              const maxActiveDate = new Date(Math.max(...validDateTimes));
              
              
              let newXMin = new Date(currentXMin);
              let newXMax = new Date(currentXMax);
              let xNeedsUpdate = false;
              
              // Expand leftward if predictions are before current view
              if (minActiveDate < currentXMin) {
                // Add 10% padding before earliest prediction
                const paddingMs = (currentXMax.getTime() - currentXMin.getTime()) * 0.1;
                newXMin = new Date(minActiveDate.getTime() - paddingMs);
                xNeedsUpdate = true;
              }
              
              // Expand rightward if predictions are after current view
              if (maxActiveDate > currentXMax) {
                // Add 10% padding after latest prediction
                const paddingMs = (currentXMax.getTime() - currentXMin.getTime()) * 0.1;
                newXMax = new Date(maxActiveDate.getTime() + paddingMs);
                xNeedsUpdate = true;
              }
              
              // Apply X-axis changes
              if (xNeedsUpdate) {
                // ✅ MOVE: Apply constraint check INSIDE the bypass
                const earliestDate = d3.min(processedData, d => d.time)!;
                if (newXMin < earliestDate) {
                  newXMin = earliestDate;
                }
                
                xScale.domain([newXMin, newXMax]);
                needsUpdate = true;
              }
            }
          });
          
          // ✅ REMEMBER: Mark these predictions as adjusted so we don't re-adjust
          setAdjustedPredictions(new Set(currentActivePredictions));
        }
      }
      
      // ✅ CLEANUP: Remove predictions that are no longer active
      if (currentActivePredictions.length === 0) {
        setAdjustedPredictions(new Set());
      }
      
      // ✅ REMOVE: Delete this conflicting constraint check
      // const earliestDate = d3.min(processedData, d => d.time)!;
      // const [xMin, xMax] = xScale.domain();
      // if (xMin < earliestDate) {
      //   const timeRange = xMax.getTime() - xMin.getTime();
      //   xScale.domain([earliestDate, new Date(earliestDate.getTime() + timeRange)]);
      // }

      // First update the line
      chartArea.select('.price-line')
        .attr('d', line(processedData));

      // Update x-axis with current scale
      svg.select('.x-axis')
        .call(d3.axisBottom(xScale) as any)
        .call(g => g.selectAll('.tick text').attr('fill', theme.chartText));

      // Update y-axis with current scale
      svg.select('.y-axis')
        .call(d3.axisLeft(yScale) as any)
        .call(g => g.selectAll('.tick text').attr('fill', theme.chartText));



      // Update the current price indicator position to always show at latest price
      // Make sure the indicator is inside the visible area of the chart
      if (currentPriceIndicatorRef.current?.circle && currentPriceIndicatorRef.current?.label && latestDataPoint) {
        const [xMin, xMax] = xScale.domain();
        const latestTime = latestDataPoint.time;

        // Only show the price indicator if the latest data point is visible
        const isLatestDataVisible = latestTime >= xMin && latestTime <= xMax;
        const visibility = isLatestDataVisible ? 0.6 : 0; // Hide if not visible

        currentPriceIndicatorRef.current.circle
          .attr('cx', xScale(latestDataPoint.time))
          .attr('cy', yScale(latestDataPoint.value))
          .attr('opacity', visibility);

        currentPriceIndicatorRef.current.label
          .attr('x', xScale(latestDataPoint.time) + 15) // ✅ Changed from + 8 to + 15 for more space
          .attr('y', yScale(latestDataPoint.value) - 8)
          .attr('opacity', visibility)
          .text(formatPrice(latestDataPoint.value));
      }



      // Update floating live price indicator if we have a live price
      if (livePriceElementsRef.current.label && livePrice) {
        // Always show the floating indicator (no visibility checks needed)
        livePriceGroup.attr('opacity', 0.95);

        livePriceElementsRef.current.label
          .text(formatPrice(Math.round(livePrice)));
        
        if (livePriceElementsRef.current.timeLabel) {
          const now = new Date();
          const timeStr = now.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
          });
          livePriceElementsRef.current.timeLabel.text(timeStr);
        }
      }

      // Call the prediction renderer with all necessary parameters
      // ✅ FIX: Use activePredictionsRef.current to always get the latest value, not the stale closure value
      renderAllPredictions({
        chartArea,
        predictions,
        activePredictions: activePredictionsRef.current,
        xScale,
        yScale,
        theme,
        chartData: processedData, // Pass processedData to renderers
        showAllDots, // Pass dashboard mode flag
        compactMode // Mobile landscape: smaller fonts and cards
      });
      
      // Render user's contest prediction point (if they have one)
      if (userContestPrediction) {
        renderUserPredictionPoint({
          chartArea,
          xScale,
          yScale,
          theme,
          chartWidth: width,
          userPrediction: userContestPrediction,
          userHandle
        });
      }
      
      // Render all contest entry dots along the contest line
      if (contestEntries && contestEntries.length > 0) {
        // Use live WebSocket price for determining who's leading (falls back to last data point)
        const currentPrice = livePrice || (processedData.length > 0 ? processedData[processedData.length - 1].value : 0);
        renderContestEntriesDots({
          chartArea,
          xScale,
          yScale,
          theme,
          entries: contestEntries,
          currentUserHandle: userHandle,
          currentPrice
        });
      }


    };

    // Add drag behavior variables
    let isDragging = false;
    let dragStartX: number;
    let dragStartY: number;
    let startDomainX: [Date, Date];
    let startDomainY: [number, number];

    // Create a SINGLE overlay for all interactions
    const interactionOverlay = chartArea.append('rect')
      .attr('class', 'interaction-overlay')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .style('cursor', 'grab')
      .on('mousedown', (event) => {
        // ✅ DRAWING MODE: Forward to DrawingMode for freehand drawing
        if (isDrawingModeRef.current && !event.shiftKey) {
          // Dispatch custom event for freehand drawing
          const [mouseX, mouseY] = d3.pointer(event);
          const customEvent = new CustomEvent('drawingModeMouseDown', {
            detail: { x: mouseX, y: mouseY, clientX: event.clientX, clientY: event.clientY },
            bubbles: true
          });
          (event.target as Element).dispatchEvent(customEvent);
          return; // Don't start pan dragging
        }
        event.preventDefault(); // Prevent text selection
        isDragging = true;
        dragStartX = d3.pointer(event)[0];
        dragStartY = d3.pointer(event)[1];
        startDomainX = xScale.domain() as [Date, Date];
        startDomainY = yScale.domain() as [number, number];
        d3.select(event.currentTarget).style('cursor', 'grabbing');
      })
      .on('mousemove', (event) => {
        // ✅ DRAWING MODE: Forward mousemove for freehand drawing
        if (isDrawingModeRef.current) {
          const [mouseX, mouseY] = d3.pointer(event);
          const customEvent = new CustomEvent('drawingModeMouseMove', {
            detail: { x: mouseX, y: mouseY, clientX: event.clientX, clientY: event.clientY },
            bubbles: true
          });
          (event.target as Element).dispatchEvent(customEvent);
        }
        
        // Handle tooltip
        const [mouseX] = d3.pointer(event);
        const date = xScale.invert(mouseX);
        const bisect = d3.bisector((d: { time: string }) => new Date(d.time)).left;
        const index = bisect(chartData, date);
        const dataPoint = chartData[index];

        if (dataPoint) {
          setTooltipData({
            x: event.offsetX,
            y: event.offsetY,
            date: new Date(dataPoint.time).toLocaleDateString(),
            price: dataPoint.value
          });
        }

        // Handle dragging
        if (isDragging) {
          const [currentX, currentY] = d3.pointer(event);
          
          // Calculate X movement and update X domain - unchanged
          const dx = dragStartX - currentX;
          const timeRange = startDomainX[1].getTime() - startDomainX[0].getTime();
          const moveAmountX = (timeRange / width) * dx;
          
          // Calculate new domains
          let newStartX = new Date(startDomainX[0].getTime() + moveAmountX);
          let newEndX = new Date(startDomainX[1].getTime() + moveAmountX);
          
          // Ensure we don't go before the first data point
          const earliestDate = d3.min(chartData, d => new Date(d.time))!;
          if (newStartX < earliestDate) {
            newStartX = earliestDate;
            newEndX = new Date(earliestDate.getTime() + timeRange);
          }
          
          xScale.domain([newStartX, newEndX]);
          
          // Calculate Y movement and update Y domain with INVERTED direction
          // Invert dy calculation - moving down should increase values (move chart up)
          const dy = dragStartY - currentY;  // Changed: swapped currentY and dragStartY
          const yRange = startDomainY[1] - startDomainY[0];
          const moveAmountY = (yRange / height) * dy;
          
          // Apply movement to Y domain
          let newMin = startDomainY[0] - moveAmountY;
          let newMax = startDomainY[1] - moveAmountY;

          // Modified: Allow negative values but prevent extremely negative values
          // ✅ FIX: Expand constraint for very low predictions like Peter Zeihan's -$1000
          if (newMin < -2000) {
            const adjustment = -2000 - newMin;
            newMin = -2000;
            newMax += adjustment;
          }

          // Prevent values from going too low in terms of maximum
          const minValue = d3.min(chartData, d => d.value) || 0;
          if (newMax < minValue * 1.1) {
            newMax = minValue * 1.1;
          }
          
          yScale.domain([newMin, newMax]);
          
          // ✅ ZOOM PERSISTENCE: Save zoom state after pan
          zoomStateRef.current.xDomain = xScale.domain() as [Date, Date];
          zoomStateRef.current.yDomain = yScale.domain() as [number, number];
          
          // Update chart with new domains
          updateChart();
        }
      })
      .on('mouseup', (event) => {
        // ✅ DRAWING MODE: Forward mouseup for freehand drawing
        if (isDrawingModeRef.current) {
          const [mouseX, mouseY] = d3.pointer(event);
          const customEvent = new CustomEvent('drawingModeMouseUp', {
            detail: { x: mouseX, y: mouseY },
            bubbles: true
          });
          (event.target as Element).dispatchEvent(customEvent);
        }
        isDragging = false;
        d3.select(event.currentTarget).style('cursor', 'grab');
      })
      .on('mouseleave', (event) => {
        isDragging = false;
        setTooltipData(null);
        d3.select(event.currentTarget).style('cursor', isDrawingModeRef.current ? 'crosshair' : 'grab');
      })
      .on('wheel', (event) => {
        // ✅ DRAWING MODE: Allow zoom with Shift key, or always allow zoom (more intuitive)
        // Zoom doesn't interfere with drawing, so we can allow it always
        handleWheelZoom(
          event,
          d3.pointer(event)[0],
          xScale,
          yScale,
          initialScaleRef.current,
          processedData,
          updateChart,
          // ✅ ZOOM PERSISTENCE: Save zoom state after wheel zoom
          (xDomain: [Date, Date], yDomain: [number, number]) => {
            zoomStateRef.current.xDomain = xDomain;
            zoomStateRef.current.yDomain = yDomain;
          }
        );
      })
      // ✅ TOUCH SUPPORT: Add touch event handlers for mobile zoom/pan
      .on('touchstart', (event) => {
        // ✅ DRAWING MODE: Single finger draws, two fingers zoom/pan
        const touches = event.touches;
        if (isDrawingModeRef.current && touches.length === 1) return; // Let DrawingMode handle single finger
        if (touches.length === 1) {
          // Single finger - pan
          event.preventDefault();
          isDragging = true;
          const touch = touches[0];
          const [touchX, touchY] = d3.pointer(touch, event.currentTarget);
          dragStartX = touchX;
          dragStartY = touchY;
          startDomainX = xScale.domain() as [Date, Date];
          startDomainY = yScale.domain() as [number, number];
        } else if (touches.length === 2) {
          // Two fingers - pinch zoom
          event.preventDefault();
          const touch1 = touches[0];
          const touch2 = touches[1];
          // Store initial pinch distance
          (interactionOverlay.node() as any).__pinchStartDist = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );
          (interactionOverlay.node() as any).__pinchStartDomainX = xScale.domain();
          (interactionOverlay.node() as any).__pinchStartDomainY = yScale.domain();
          // Store center point
          const [cx, cy] = d3.pointer(touch1, event.currentTarget);
          const [cx2, cy2] = d3.pointer(touch2, event.currentTarget);
          (interactionOverlay.node() as any).__pinchCenterX = (cx + cx2) / 2;
          (interactionOverlay.node() as any).__pinchCenterY = (cy + cy2) / 2;
        }
      })
      .on('touchmove', (event) => {
        const touches = event.touches;
        if (touches.length === 1 && isDragging) {
          // Single finger pan
          event.preventDefault();
          const touch = touches[0];
          const [currentX, currentY] = d3.pointer(touch, event.currentTarget);
          
          // Calculate X movement
          const dx = dragStartX - currentX;
          const timeRange = startDomainX[1].getTime() - startDomainX[0].getTime();
          const moveAmountX = (timeRange / width) * dx;
          
          let newStartX = new Date(startDomainX[0].getTime() + moveAmountX);
          let newEndX = new Date(startDomainX[1].getTime() + moveAmountX);
          
          const earliestDate = d3.min(chartData, d => new Date(d.time))!;
          if (newStartX < earliestDate) {
            newStartX = earliestDate;
            newEndX = new Date(earliestDate.getTime() + timeRange);
          }
          xScale.domain([newStartX, newEndX]);
          
          // Calculate Y movement (inverted)
          const dy = dragStartY - currentY;
          const yRange = startDomainY[1] - startDomainY[0];
          const moveAmountY = (yRange / height) * dy;
          
          let newMin = startDomainY[0] - moveAmountY;
          let newMax = startDomainY[1] - moveAmountY;
          
          if (newMin < -2000) {
            const adjustment = -2000 - newMin;
            newMin = -2000;
            newMax += adjustment;
          }
          
          yScale.domain([newMin, newMax]);
          
          // ✅ ZOOM PERSISTENCE: Save zoom state after touch pan
          zoomStateRef.current.xDomain = xScale.domain() as [Date, Date];
          zoomStateRef.current.yDomain = yScale.domain() as [number, number];
          
          updateChart();
        } else if (touches.length === 2) {
          // Pinch zoom
          event.preventDefault();
          const touch1 = touches[0];
          const touch2 = touches[1];
          const currentDist = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );
          
          const startDist = (interactionOverlay.node() as any).__pinchStartDist;
          const startDomainXPinch = (interactionOverlay.node() as any).__pinchStartDomainX;
          const startDomainYPinch = (interactionOverlay.node() as any).__pinchStartDomainY;
          const centerX = (interactionOverlay.node() as any).__pinchCenterX;
          const centerY = (interactionOverlay.node() as any).__pinchCenterY;
          
          if (startDist && startDomainXPinch && startDomainYPinch) {
            const scale = startDist / currentDist; // Invert for natural pinch
            
            // Calculate center in data coordinates from start domains
            const centerTime = startDomainXPinch[0].getTime() + 
              (centerX / width) * (startDomainXPinch[1].getTime() - startDomainXPinch[0].getTime());
            const centerPrice = startDomainYPinch[1] - 
              (centerY / height) * (startDomainYPinch[1] - startDomainYPinch[0]);
            
            // Scale around center point
            const timeRange = startDomainXPinch[1].getTime() - startDomainXPinch[0].getTime();
            const newTimeRange = timeRange * scale;
            
            const priceRange = startDomainYPinch[1] - startDomainYPinch[0];
            const newPriceRange = priceRange * scale;
            
            // Calculate new domains centered on pinch point
            const newXStart = new Date(centerTime - (centerX / width) * newTimeRange);
            const newXEnd = new Date(centerTime + (1 - centerX / width) * newTimeRange);
            
            // Apply X domain with constraint
            const earliestDate = d3.min(chartData, d => new Date(d.time))!;
            if (newXStart < earliestDate) {
              xScale.domain([earliestDate, new Date(earliestDate.getTime() + newTimeRange)]);
            } else {
              xScale.domain([newXStart, newXEnd]);
            }
            
            // Apply Y domain
            const newYMin = centerPrice - (1 - centerY / height) * newPriceRange;
            const newYMax = centerPrice + (centerY / height) * newPriceRange;
            yScale.domain([Math.max(newYMin, -2000), newYMax]);
            
            // ✅ ZOOM PERSISTENCE: Save zoom state after pinch zoom
            zoomStateRef.current.xDomain = xScale.domain() as [Date, Date];
            zoomStateRef.current.yDomain = yScale.domain() as [number, number];
            
            updateChart();
          }
        }
      })
      .on('touchend', (event) => {
        isDragging = false;
        // Clean up pinch state
        const node = interactionOverlay.node() as any;
        delete node.__pinchStartDist;
        delete node.__pinchStartDomainX;
        delete node.__pinchStartDomainY;
        delete node.__pinchCenterX;
        delete node.__pinchCenterY;
      })
      // ✅ DRAWING MODE: Handle clicks for placing prediction points
      .on('click', (event) => {
        if (!isDrawingModeRef.current) return; // Only handle clicks in drawing mode
        
        // Get click position relative to chart
        const [mouseX, mouseY] = d3.pointer(event);
        
        // Convert to data coordinates
        const time = xScale.invert(mouseX);
        const price = yScale.invert(mouseY);
        
        // Dispatch a custom event that DrawingMode can listen to
        const customEvent = new CustomEvent('drawingModeClick', {
          detail: { x: mouseX, y: mouseY, time, price },
          bubbles: true
        });
        (event.target as Element).dispatchEvent(customEvent);
      });

    // Handle mouseup outside chart
    d3.select('body').on('mouseup.chart', () => {
      if (isDragging) {
        isDragging = false;
        interactionOverlay.style('cursor', 'grab');
      }
    });

    // Remove any duplicate overlays
    chartArea.selectAll('.interaction-overlay').filter((_, i, nodes) => 
      nodes[i] !== interactionOverlay.node()
    ).remove();

    // Initial draw - important for
    updateChart();

    // Clean up
    return () => {
      d3.select('body').on('mouseup.chart', null);
    };
  }, [chartData, predictions, isDarkMode, theme, dimensions]); // ✅ Added dimensions to re-render on resize

  // 🔧 Separate effect for activePredictions changes - only re-renders predictions, preserves zoom
  useEffect(() => {
    if (!chartAreaRef.current || !scalesRef.current || !processedDataRef.current.length) {
      return;
    }
    
    const { xScale, yScale } = scalesRef.current;
    const chartArea = chartAreaRef.current;
    const processedData = processedDataRef.current;
    
    // Re-render predictions with current (preserved) scales
    renderAllPredictions({
      chartArea,
      predictions,
      activePredictions: activePredictions || [],
      xScale,
      yScale,
      theme,
      chartData: processedData,
      showAllDots,
      compactMode
    });
    
    // Re-render user's contest prediction point
    if (userContestPrediction) {
      const container = chartArea.node()?.parentElement;
      const width = container?.clientWidth || 800;
      renderUserPredictionPoint({
        chartArea,
        xScale,
        yScale,
        theme,
        chartWidth: width - 130, // Account for margins
        userPrediction: userContestPrediction,
        userHandle
      });
    }
    
    // Re-render contest entry dots
    if (contestEntries && contestEntries.length > 0) {
      // Use live WebSocket price for determining who's leading (falls back to last data point)
      const currentPrice = livePrice || (processedData.length > 0 ? processedData[processedData.length - 1].value : 0);
      renderContestEntriesDots({
        chartArea,
        xScale,
        yScale,
        theme,
        entries: contestEntries,
        currentUserHandle: userHandle,
        currentPrice
      });
    }
  }, [activePredictions, predictions, theme, showAllDots, userContestPrediction, userHandle, contestEntries, livePrice]);

  // Update live price floating indicator AND price line tip when price changes
  useEffect(() => {
    if (
      !svgRef.current || 
      !livePriceElementsRef.current.label ||
      !livePrice
    ) return;

    const svg = d3.select(svgRef.current);
    const liveGroup = svg.select('.live-price-floating-indicator');
    
    // Always show floating indicator (no range checks needed)
    liveGroup.attr('opacity', 0.95);

    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });

    // Update price label - remove decimals 
    if (livePriceElementsRef.current.label) {
      livePriceElementsRef.current.label
        .text(formatPrice(Math.round(livePrice)));
    }

    // Update time label
    if (livePriceElementsRef.current.timeLabel) {
      livePriceElementsRef.current.timeLabel
        .text(timeStr);
    }
    
    // Update price line tip with live price
    if (chartAreaRef.current && scalesRef.current && processedDataRef.current.length > 0 && lineGeneratorRef.current) {
      const { xScale, yScale } = scalesRef.current;
      const processedData = processedDataRef.current;
      
      // Create a new line generator that uses current scales
      const line = d3.line<{ time: Date; value: number }>()
        .x(d => xScale(d.time))
        .y(d => yScale(d.value))
        .curve(d3.curveMonotoneX);
      
      // Add live price as a "virtual" point at current time
      const livePoint = { time: now, value: livePrice };
      const dataWithLivePoint = [...processedData, livePoint];
      
      // Update the price line path with smooth transition
      chartAreaRef.current.select('.price-line')
        .datum(dataWithLivePoint)
        .transition()
        .duration(200)
        .ease(d3.easeLinear)
        .attr('d', line);
      
      // Update current price indicator dot position
      if (currentPriceIndicatorRef.current?.circle && currentPriceIndicatorRef.current?.label) {
        const [xMin, xMax] = xScale.domain();
        const isTimeVisible = now >= xMin && now <= xMax;
        const visibility = isTimeVisible ? 0.8 : 0;
        
        currentPriceIndicatorRef.current.circle
          .transition()
          .duration(200)
          .ease(d3.easeLinear)
          .attr('cx', xScale(now))
          .attr('cy', yScale(livePrice))
          .attr('opacity', visibility);
        
        currentPriceIndicatorRef.current.label
          .transition()
          .duration(200)
          .ease(d3.easeLinear)
          .attr('x', xScale(now) + 15)
          .attr('y', yScale(livePrice) - 8)
          .attr('opacity', visibility)
          .text(formatPrice(livePrice));
      }
    }
  }, [livePrice]);

  // ✅ DRAWING MODE: Handlers for click-to-place predictions
  const handleDrawingToggle = useCallback(() => {
    setIsDrawingMode(prev => !prev);
    if (isDrawingMode) {
      // Exiting drawing mode - clear points
      setDrawingPoints([]);
      setDrawingFreehandPaths([]);
      setDrawingImageBase64(null);
    }
  }, [isDrawingMode]);

  const handleDrawingComplete = useCallback((result: DrawingResult) => {
    console.log('📍 Drawing complete:', result);
    setDrawingPoints(result.points);
    setDrawingModeType(result.mode || 'points');
    setDrawingFreehandPaths(result.freehandPaths || []);
    setDrawingImageBase64(result.imageBase64 || null);
    setShowDrawingConfirm(true);
    setIsDrawingMode(false);
  }, []);

  // Share drawing as screenshot without submitting
  const handleDrawingShare = useCallback(async (result: DrawingResult) => {
    console.log('📸 Sharing drawing:', result);
    
    try {
      // Keep drawing visible for screenshot
      // Use html2canvas or similar to capture the chart
      const chartContainer = containerRef.current;
      if (!chartContainer) {
        alert('Could not capture chart');
        return;
      }
      
      // Dynamic import of html2canvas
      const html2canvas = (await import('html2canvas')).default;
      
      const canvas = await html2canvas(chartContainer, {
        backgroundColor: '#1a1a1a',
        scale: 2, // Higher quality
        logging: false,
        useCORS: true
      });
      
      // Convert to blob and trigger download
      canvas.toBlob((blob) => {
        if (!blob) {
          alert('Could not create image');
          return;
        }
        
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bitcoin-chart-${result.mode}-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        // Clear drawing after share
        setDrawingPoints([]);
        setIsDrawingMode(false);
        
        alert('📸 Chart image downloaded! Share it anywhere.');
      }, 'image/png');
      
    } catch (error) {
      console.error('Error sharing:', error);
      alert('Failed to create image. Try again.');
    }
  }, []);

  const handleDrawingConfirm = useCallback(async (interpretation: any) => {
    console.log('✅ Submitting drawn prediction:', interpretation);
    
    try {
      // Submit to submissions API (user-submitted predictions)
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expert_name: 'Anonymous User', // User-submitted prediction
          prediction_type: interpretation.prediction_type,
          direction: interpretation.direction,
          price_target: interpretation.price_target,
          price_min: interpretation.price_min,
          price_max: interpretation.price_max,
          target_time: interpretation.end_date || interpretation.target_date,
          notes: interpretation.interpretation,
          user_thesis: interpretation.userThesis, // User's own explanation
          sequence_data: interpretation.sequence_data,
          drawing_points: interpretation.drawing_points, // Store exact drawing for rendering
          source: 'drawing',
          submitted_by: 'user'
        })
      });

      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit prediction');
      }

      console.log('✅ Prediction submitted:', data);
      
      // Close modal and clear state
      setShowDrawingConfirm(false);
      setDrawingPoints([]);
      
      // TODO: Show success toast and refresh predictions
      alert('Prediction submitted successfully! It will appear after review.');
      
    } catch (error: any) {
      console.error('❌ Error submitting prediction:', error);
      alert('Failed to submit prediction: ' + (error.message || 'Please try again.'));
    }
  }, []);

  const handleDrawingCancel = useCallback(() => {
    setShowDrawingConfirm(false);
    setDrawingPoints([]);
    setDrawingFreehandPaths([]);
    setDrawingImageBase64(null);
  }, []);

  // 🐰 EASTER EGG: Handle egg collection with Bitcoin explosion
  const handleEggClick = useCallback((eggId: string) => {
    const clickedEgg = easterEggs.find(egg => egg.id === eggId);
    if (!clickedEgg || clickedEgg.collected) return;
    
    // Create Bitcoin explosion at egg position
    const explosion = {
      id: `explosion-${Date.now()}-${Math.random()}`,
      x: clickedEgg.x,
      y: clickedEgg.y,
      timestamp: Date.now()
    };
    
    setBitcoinExplosions(prev => [...prev, explosion]);
    
    // Remove explosion after 2 seconds
    setTimeout(() => {
      setBitcoinExplosions(prev => prev.filter(e => e.id !== explosion.id));
    }, 2000);
    
    setEasterEggs(prevEggs => 
      prevEggs.map(egg => 
        egg.id === eggId ? { ...egg, collected: true } : egg
      )
    );
    
    const newCollected = easterEggCollected + 1;
    setEasterEggCollected(newCollected);
    
    // If they collected 5 eggs, show success popup!
    if (newCollected >= 5) {
      setShowEasterEggPopup(true);
      const timestamp = new Date().toISOString();
      const uniqueCode = Math.random().toString(36).substr(2, 8).toUpperCase();
      localStorage.setItem('proofofprice-easter-egg-2026', JSON.stringify({
        found: true,
        timestamp,
        code: uniqueCode
      }));
      
      // Clean up animation
      if (easterEggAnimationRef.current) {
        cancelAnimationFrame(easterEggAnimationRef.current);
      }
    }
  }, [easterEggs, easterEggCollected]);

  // Get current price for drawing mode - MUST be real data, no fallback
  const currentPriceForDrawing = livePrice || chartData[chartData.length - 1]?.value || null;

  // If no data, show a message
  if (chartData.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <StyledContainer ref={containerRef} $isScreenshotMode={isScreenshotMode}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'center', 
            alignItems: 'center',
            height: '100%',
            color: theme.text,
            flexDirection: 'column'
          }}>
            <div style={{ fontSize: '18px', marginBottom: '10px' }}>No price data available</div>
            <div style={{ fontSize: '14px', color: theme.subText }}>
              Please ensure the database connection is working correctly
            </div>
          </div>
        </StyledContainer>
      </ThemeProvider>
    );
  }   

  return (
    <ThemeProvider theme={theme}>
      <StyledContainer ref={containerRef} data-show-all-dots={showAllDots} $isScreenshotMode={isScreenshotMode}>
        {/* Move PriceLegend to top - hide in screenshot mode */}
        {!isScreenshotMode && (
        <div style={{
        position: 'absolute',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        height: '30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: '8px',
        padding: '4px 16px',
        zIndex: 10,
        color: theme.text,
      }}>
        <PriceLegend theme={theme} />
      </div>
        )}
      
      <svg 
        ref={svgRef} 
        style={{ 
          width: isScreenshotMode ? '100vw' : '100%', 
          height: isScreenshotMode ? '100vh' : '100%',
          display: 'block'
        }} 
      />
      
      {/* ✅ DRAWING MODE: Click-to-place prediction input */}
      <DrawingMode
        isActive={isDrawingMode}
        onToggle={handleDrawingToggle}
        onComplete={handleDrawingComplete}
        onShare={handleDrawingShare}
        xScale={scalesRef.current?.xScale}
        yScale={scalesRef.current?.yScale}
        chartArea={chartAreaRef.current?.node() || null}
        currentPrice={currentPriceForDrawing}
        theme={theme}
      />
      
      {/* ✅ DRAWING CONFIRM: Grok AI interpretation modal */}
      <DrawingConfirmModal
        isOpen={showDrawingConfirm}
        onClose={handleDrawingCancel}
        onConfirm={handleDrawingConfirm}
        points={drawingPoints}
        drawingMode={drawingModeType}
        freehandPaths={drawingFreehandPaths}
        imageBase64={drawingImageBase64}
        currentPrice={currentPriceForDrawing}
        theme={theme}
      />
      
      {tooltipData && (
        <div style={{
          position: 'absolute',
          left: tooltipData.x + 'px',
          top: tooltipData.y + 'px',
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          padding: '8px',
          borderRadius: '4px',
          pointerEvents: 'none',
          transform: 'translate(-50%, -100%)',
          zIndex: 1000,
        }}>
          <div>{tooltipData.date}</div>
          <div style={{ color: theme.primary }}>{formatPrice(tooltipData.price)}</div>
        </div>
      )}

      {/* 🐰 EASTER EGGS RENDERING - BIGGER DESKTOP TARGETS TOO */}
      {easterEggs.filter(egg => !egg.collected).map((egg) => (
        <div
          key={egg.id}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEggClick(egg.id);
          }}
          style={{
            position: 'absolute',
            left: egg.x + 'px',
            top: egg.y + 'px',
            width: '40px', // Bigger click target for desktop too
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 999,
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0,0,0,0)', // Transparent tap area
          }}
          onMouseEnter={(e) => {
            const eggDiv = e.currentTarget.querySelector('div') as HTMLDivElement;
            if (eggDiv) eggDiv.style.transform = 'scale(1.3)';
          }}
          onMouseLeave={(e) => {
            const eggDiv = e.currentTarget.querySelector('div') as HTMLDivElement;
            if (eggDiv) eggDiv.style.transform = 'scale(1)';
          }}
          title="🐰 Click to collect Easter egg!"
        >
          {/* Visual egg inside the larger click area */}
          <div
            style={{
              width: '28px', // Visual egg size
              height: '34px',
              background: egg.color,
              borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              border: '3px solid rgba(255,255,255,0.9)',
              animation: 'bounce 1.2s ease-in-out infinite alternate',
              transition: 'transform 0.2s ease',
              pointerEvents: 'none', // Let parent handle clicks
            }}
          />
        </div>
      ))}

      {/* 🐰 EASTER EGG SUCCESS POPUP */}
      {showEasterEggPopup && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            background: theme.surface,
            border: `3px solid ${theme.primary}`,
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'center',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            animation: 'bounce 0.6s ease-out',
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              animation: 'spin 1s linear infinite'
            }}>
              🐰🥚
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: theme.primary,
              marginBottom: '16px'
            }}>
              YOU FOUND THE EASTER EGG!
            </div>
            <div style={{ 
              fontSize: '16px', 
              color: theme.text,
              marginBottom: '24px',
              lineHeight: '1.4'
            }}>
              🎉 Congratulations! You discovered the secret Easter Bunny prediction!<br/><br/>
              📸 <strong>TAKE A SCREENSHOT NOW!</strong><br/>
              Then tweet it @proofofprice to claim your <strong>1000 SATS</strong>! 💰
            </div>
            <button 
              onClick={() => setShowEasterEggPopup(false)}
              style={{
                background: theme.primary,
                color: theme.background,
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Awesome! 🚀
            </button>
          </div>
        </div>
      )}

      {/* 🐰 EASTER EGG INSTRUCTIONS POPUP */}
      {showEasterEggInstructions && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: theme.surface,
          border: `3px solid ${theme.primary}`,
          borderRadius: '12px',
          padding: '16px 24px',
          textAlign: 'center',
          zIndex: 10001,
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          animation: 'bounce 0.5s ease-out',
        }}>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: theme.primary,
            marginBottom: '8px'
          }}>
            🐰 EASTER EGG HUNT! 🥚
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: theme.text,
            lineHeight: '1.4'
          }}>
            Click the falling eggs to collect them!<br/>
            Collect <strong>all 21 eggs</strong> to win 1000 sats! 🏆
          </div>
        </div>
      )}
      </StyledContainer>
    </ThemeProvider>
  );
};
